import { inject, injectable } from "inversify";
import { Observable, ObservableArray, PureComputed, Subscribable } from "knockout";
import componentModel from "../../decorators/componentModel";
import { wccModules } from "../../enums/wccModules";
import { ImageHelpers } from "../../helpers/images";
import { EffectsContainer } from "../../mixins/withEffects";
import ExpandableControlVM, { ExpandableControlVMConfig } from "../expandableControl/expandableControl";
import "./emojisPicker.html";
import "./emojisPicker.scss";
import { EmojisPickerActivityEmojis } from "./library/categories/activity";
import { EmojisPickerFlagsEmojis } from "./library/categories/flags";
import { EmojisPickerFoodEmojis } from "./library/categories/food";
import { EmojisPickerNatureEmojis } from "./library/categories/nature";
import { EmojisPickerObjectsEmojis } from "./library/categories/objects";
import { EmojisPickerPeopleEmojis } from "./library/categories/people";
import { EmojisPickerSymbolsEmojis } from "./library/categories/symbols";
import { EmojisPickerTravelEmojis } from "./library/categories/travel";
import EmojisPickerCategory from "./models/category";
import EmojisPickerEmoji from "./models/emoji";

export interface EmojisPickerConfig {
    pick: Action<[emoji: EmojisPickerEmoji]>
}

export class EmojisPicker {
    onPick: Action<[emoji: EmojisPickerEmoji]>

    constructor(config: EmojisPickerConfig) {
        this.onPick = config.pick;
    }
}

@injectable()
@componentModel(EmojisPicker)
export default class EmojisPickerVM {
    private isRendered: Subscribable<boolean>
    private isActive: PureComputed<boolean>    

    activeCategory: Observable<EmojisPickerCategory>
    categories: ObservableArray<EmojisPickerCategory>
    emojis: Subscribable<EmojisPickerEmoji[]>

    areEmojisLoaded: Subscribable<boolean>

    expandableControlConfig: ExpandableControlVMConfig
    expandableControl = ko.observable<ExpandableControlVM>();    

    constructor(
        @inject(wccModules.componentConfig) private picker: EmojisPicker,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.categories = ko.observableArray([
            new EmojisPickerCategory({ title: 'People', icon: 'fal fa-smile', emojis: EmojisPickerPeopleEmojis }),
            new EmojisPickerCategory({ title: 'Nature', icon: 'fal fa-leaf', emojis: EmojisPickerNatureEmojis }),
            new EmojisPickerCategory({ title: 'Food', icon: 'fal fa-utensils', emojis: EmojisPickerFoodEmojis }),
            new EmojisPickerCategory({ title: 'Activity', icon: 'fal fa-futbol', emojis: EmojisPickerActivityEmojis }),
            new EmojisPickerCategory({ title: 'Travel', icon: 'fal fa-car', emojis: EmojisPickerTravelEmojis }),
            new EmojisPickerCategory({ title: 'Objects', icon: 'fal fa-cogs', emojis: EmojisPickerObjectsEmojis }),
            new EmojisPickerCategory({ title: 'Symbols', icon: 'fal fa-info-circle', emojis: EmojisPickerSymbolsEmojis }),
            new EmojisPickerCategory({ title: 'Flags', icon: 'fal fa-flag', emojis: EmojisPickerFlagsEmojis })
        ]);

        this.activeCategory = ko.observable(this.categories()[0]);
        this.emojis = this.activeCategory.pluck(c => c.emojis).extend({ deferred: true });

        this.isRendered = this.expandableControl.pluck(c => c.isBodyRendered, false);
        this.isActive = this.expandableControl.proxy(c => c.isActive).default(false);

        this.areEmojisLoaded = ko.fromPromise(async (category, isRendered) => {
            if (isRendered) {
                const tasks = category.emojis().map(async emoji => {
                    try {
                        await ImageHelpers.waitImage(ImageHelpers.getImage(emoji.url(), true));
                    } catch (ex) {
                        console.error(ex)
                    }
                });

                await Promise.all(tasks);

                return true;
            }

            return false;
        }, [this.activeCategory, this.isRendered], false);

        this.expandableControlConfig = {
            bodyCSS: 'wcc-emojispicker__body'
        }

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-emojispicker': true
            }
        });        
    }

    selectCategory = (category: EmojisPickerCategory) => {
        this.activeCategory(category);
    }

    pick = (emoji: EmojisPickerEmoji, e: JQueryEventObject) => {
        if (!e.shiftKey)
            this.isActive(false);

        this.picker.onPick(emoji);
    }
}